import { Combobox } from "@headlessui/react";
import { useRouter } from "next/router";
import Link from "next/link";
import { useDebounce } from "@uidotdev/usehooks";
import React, { useContext, useEffect } from "react";
import { useRef } from "react";
import { useState } from "react";
import * as Text from "@/components/text";
import { XCircleIcon, SearchIcon } from "@heroicons/react/solid";
import {
  IniciativaItemContext,
  IniciativaItemDispatchContext,
  setKeyword,
} from "stores/IniciativasContextProvider";
import { useSearchEntries } from "@/actions/searchActions";
import { localeTexts} from "@/utils/locale";

export function Skeletons() {
  const router = useRouter();
  const [locale, setLocale] = useState("es-MX");
  const t = localeTexts[locale]?.search || {};

    // Configuration for theme name prefixes

  const excludedPrefixes = {
  'es-MX': ['mex', 'línea'],
  'en-US': ['mex', 'line']
  };

  // Sync locale with URL and localStorage

  useEffect(() => {
  if (!router.isReady) return;
  const urlLocale = router.query.locale;
  const storedLocale = typeof window !== "undefined"
  ? localStorage.getItem("amb-locale")
  : null;

  const finalLocale = urlLocale || storedLocale || "es-MX";
  setLocale(finalLocale);
  localStorage.setItem("amb-locale", finalLocale);
  }, [router.isReady, router.query.locale]);


  return (
    <div className="flex w-full animate-pulse flex-row items-center gap-2 rounded border border-gray-200 p-1 shadow dark:border-gray-700 md:p-2 md:pl-10">
      <svg
        className="h-12 w-12 text-gray-200 dark:text-gray-600"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        fill="currentColor"
        viewBox="0 0 640 512"
      >
        <path d="M480 80C480 35.82 515.8 0 560 0C604.2 0 640 35.82 640 80C640 124.2 604.2 160 560 160C515.8 160 480 124.2 480 80zM0 456.1C0 445.6 2.964 435.3 8.551 426.4L225.3 81.01C231.9 70.42 243.5 64 256 64C268.5 64 280.1 70.42 286.8 81.01L412.7 281.7L460.9 202.7C464.1 196.1 472.2 192 480 192C487.8 192 495 196.1 499.1 202.7L631.1 419.1C636.9 428.6 640 439.7 640 450.9C640 484.6 612.6 512 578.9 512H55.91C25.03 512 .0006 486.1 .0006 456.1L0 456.1z" />
      </svg>

      <div>
        <div className="mb-2 h-2.5 w-32 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2 w-48 rounded-full bg-gray-200 dark:bg-gray-700"></div>
      </div>
    </div>
  );
}

export function Badge({ label }) {
  return (
    <Text.Body
      as="span"
      className="flex items-center justify-center rounded-full bg-neutral-100 px-2"
      size="sm"
    >
      {label}
    </Text.Body>
  );
}

export default function SearchBarBlog() {
  const router = useRouter();
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [value, setBounce] = useState("");
  const iValue = useRef(true);
  const [query, setQuery] = useState("");
  const dispatch = useContext(IniciativaItemDispatchContext);
  const { selectedItem, keyword } = useContext(IniciativaItemContext);

  const debouncedInput = useDebounce(query, 500);
  const { data: entries, error, isLoading } = useSearchEntries(debouncedInput);
 
  const [locale, setLocale] = useState("es-MX");
  const t = localeTexts[locale]?.search || {};

      // Configuration for theme name prefixes

  const excludedPrefixes = {
  'es-MX': ['mex', 'línea'],
  'en-US': ['mex', 'line']
  };

  // Sync locale with URL and localStorage

  useEffect(() => {
  if (!router.isReady) return;
  const urlLocale = router.query.locale;
  const storedLocale = typeof window !== "undefined"
  ? localStorage.getItem("amb-locale")
  : null;

  const finalLocale = urlLocale || storedLocale || "es-MX";
  setLocale(finalLocale);
  localStorage.setItem("amb-locale", finalLocale);
  }, [router.isReady, router.query.locale]);

  return (
    <>
      <Combobox
        as="div"
        className="relative w-full"
        value={selectedEntry}
        onChange={setSelectedEntry}
      >
        <Combobox.Input
          placeholder={t.placeHolderPublicaciones}
          className="relative h-full w-full border border-gray-300 p-2 text-neutral-1000 focus:outline-0 focus-visible:outline-dashed md:h-11 lg:flex-1"
          onChange={(event) => setQuery(event.target.value)}
        />
        <SearchIcon className="absolute right-3  top-3 hidden h-5 w-5 cursor-pointer hover:text-slate-600 md:block" />
        <Combobox.Options className="absolute top-full max-h-[500px] w-full space-y-2 overflow-scroll bg-white p-5 shadow-md">
          {entries?.data?.length ? (
            entries?.data?.map((entry, index) => (
              <Combobox.Option key={entry?.id || index} as="div">
                <Link
                  className="grid grid-cols-4 gap-2  hover:bg-slate-100 md:grid-cols-10  md:gap-5 "
                  value={entry?.attributes?.titulo}
                  href={`/blog/${entry?.attributes?.slug}`}
                >
                  <div className="col-span-1">
                    <img
                      alt={entry?.attributes?.titulo}
                      className="aspect-square w-full object-cover"
                      src={
                        entry?.attributes?.cover_image?.data?.attributes?.url
                      }
                    />
                  </div>
                  <div className="col-span-3 border-b border-gray-200 md:col-span-9">
                    {entry?.attributes?.categorias?.data[0]?.attributes
                      ?.name && (
                      <div className="inline-block">
                        <Badge
                          label={
                            entry?.attributes?.categorias?.data[0]?.attributes
                              .name
                          }
                        />
                      </div>
                    )}
                    <Text.Body className="font-semibold" as="p" size="md">
                      {entry?.attributes?.titulo}
                    </Text.Body>
                    <Text.Body size="md" className="line-clamp-1">
                      {entry?.attributes?.subtitulo}
                    </Text.Body>
                  </div>
                </Link>
              </Combobox.Option>
            ))
          ) : (
            <Skeletons />
          )}

          <Text.Link
            onClick={() => {
              const { query: q } = router;
              router.push({
                pathname: "/blog",
                query: { ...q, buscar: query },
              });
            }}
            label="Ver todos los resultados"
          />
        </Combobox.Options>
      </Combobox>
    </>
  );
}
